<template>
  <div class="flex flex-col font-ds">
    <div class="px-5 pt-4.5 pb-3.5 border-b border-border">
      <slot name="heading" />
      <!--
        "all dimensions" is not decoration. Tag pickers in this app have
        historically been scoped to one or two dimensions, so the field has to
        say up front that this one is not.
      -->
      <DSInputGroup :class="$slots.heading && 'mt-2'">
        <DSInputGroupAddon>
          <Search class="size-3.5" />
        </DSInputGroupAddon>
        <DSInputGroupInput
          v-model="query"
          type="search"
          :placeholder
          :aria-label="placeholder"
        />
        <DSInputGroupAddon align="inline-end">
          <DSInputGroupText>all dimensions</DSInputGroupText>
        </DSInputGroupAddon>
      </DSInputGroup>
    </div>

    <div class="flex flex-col gap-4.5 max-h-104 overflow-y-auto px-5 py-4">
      <section v-if="visibleSuggestions.length || loading || askable">
        <h4 class="mb-2.5 text-xs font-bold uppercase tracking-ds-caps text-muted-foreground">
          Suggested
        </h4>
        <ul class="flex flex-col gap-2">
          <!--
            The section appears with the question, not with the answer: opening
            the picker mid-decision would otherwise show no sign that tags are
            coming, and the rows would then push the repository's own list down
            under the user's cursor.
          -->
          <li v-if="loading">
            <p class="flex items-center gap-3 p-3 bg-accent border border-accent-foreground/20 rounded-ds-md">
              <DSThinking
                label="Looking for tags worth applying…"
                icon-class="size-4"
                class="text-sm font-bold"
              />
            </p>
          </li>
          <!--
            The ask, in the same section rather than a separate one: it stands
            exactly where the row above and the rows below it land once the
            assistant has actually been asked — see TagChips for the sibling of
            this same trigger, outside the dialog.
          -->
          <li v-else-if="askable">
            <button
              type="button"
              class="flex items-center gap-3 w-full p-3 text-left cursor-pointer
                bg-accent border border-accent-foreground/20 rounded-ds-md
                transition-colors duration-ds-fast ease-ds-standard
                hover:bg-accent-foreground/10
                focus-visible:outline-none focus-visible:shadow-ds-focus"
              @click="emit('ask')"
            >
              <Sparkles class="size-4 shrink-0 text-brand" />
              <span class="flex-1 min-w-0 text-sm font-bold text-accent-foreground">
                Ask AI
              </span>
            </button>
          </li>
          <li
            v-for="suggestion in visibleSuggestions"
            :key="tagLabel(suggestion)"
          >
            <button
              type="button"
              class="flex items-center gap-3 w-full p-3 text-left cursor-pointer
                bg-accent border border-accent-foreground/20 rounded-ds-md
                transition-colors duration-ds-fast ease-ds-standard
                hover:bg-accent-foreground/10
                focus-visible:outline-none focus-visible:shadow-ds-focus"
              @click="emit('toggle', suggestion)"
            >
              <Sparkles class="size-4 shrink-0 text-brand" />
              <span class="flex-1 min-w-0">
                <span class="flex items-center gap-1.5 min-w-0">
                  <span class="truncate text-sm font-bold text-accent-foreground">
                    {{ tagLabel(suggestion) }}
                  </span>
                  <!--
                    A pair this repository does not have yet. Said before it is
                    accepted, because accepting it adds a value to a dimension's
                    vocabulary — a different act from applying one that exists.
                  -->
                  <DSBadge
                    v-if="suggestion.isNew"
                    tone="brand"
                    size="sm"
                    class="shrink-0 bg-card"
                  >
                    new
                  </DSBadge>
                </span>
                <span class="block text-xs font-medium text-accent-foreground/75">
                  {{ suggestion.reason }}
                </span>
              </span>
              <span
                class="inline-flex items-center justify-center size-6.5 shrink-0 rounded-ds-sm"
                :class="isSelected(suggestion)
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card border border-accent-foreground/20 text-brand'"
              >
                <Check
                  v-if="isSelected(suggestion)"
                  class="size-3.5"
                />
                <Plus
                  v-else
                  class="size-3.5"
                />
              </span>
            </button>
          </li>
        </ul>
      </section>

      <section v-if="matches.length">
        <h4 class="mb-2.5 text-xs font-bold uppercase tracking-ds-caps text-muted-foreground">
          In this repository · {{ matches.length }}
          {{ matches.length === 1 ? 'match' : 'matches' }}
        </h4>
        <ul class="flex flex-col">
          <li
            v-for="(match, index) in matches"
            :key="match.id || tagLabel(match)"
          >
            <button
              type="button"
              class="flex items-center justify-between gap-3 w-full px-0.5 py-2.5 text-left cursor-pointer
                transition-colors duration-ds-fast ease-ds-standard
                hover:text-accent-foreground
                focus-visible:outline-none focus-visible:shadow-ds-focus"
              :class="index < matches.length - 1 && 'border-b border-border'"
              @click="emit('toggle', match)"
            >
              <span class="flex items-center gap-2 min-w-0">
                <Check
                  v-if="isSelected(match)"
                  class="size-3.5 shrink-0 text-success"
                />
                <span class="truncate text-sm font-medium text-foreground">
                  {{ match.tag_value }}
                </span>
                <!--
                  Created here, not saved. Said on the row because the list is
                  otherwise a list of what the repository has, and this one is
                  not that yet.
                -->
                <DSBadge
                  v-if="isPending(match)"
                  tone="brand"
                  size="sm"
                  class="shrink-0"
                >
                  unsaved
                </DSBadge>
              </span>
              <!-- The dimension, and only that: which tag this is, not how used it is. -->
              <span class="shrink-0 text-xs font-medium text-muted-foreground">
                {{ match.tag_name }}
              </span>
            </button>
          </li>
        </ul>
      </section>

      <p
        v-else-if="query.trim()"
        class="py-4 text-center text-sm font-medium text-muted-foreground"
        role="status"
      >
        No tag matches “{{ query.trim() }}”.
      </p>

      <!--
        Creating inline, with the dimension chosen rather than assumed — "any
        dimension" means nothing if a new tag can only land in one.
      -->
      <!--
        Hidden once the exact pair is in the list, pending or saved. `create` is
        a toggle at the other end, so offering it again would let a second click
        take the tag back off — the opposite of what the button says.
      -->
      <div
        v-if="query.trim() && !alreadyListed"
        class="flex flex-wrap items-center gap-2.5 p-3 border border-dashed border-input rounded-ds-md hover:bg-accent cursor-pointer"
        :disabled="!effectiveDimension"
        @click="handleCreate"
      >
        <Plus class="size-4 shrink-0 text-brand" />
        <span class="flex-1 min-w-0 truncate text-sm font-bold text-foreground">
          Create “{{ query.trim() }}”
        </span>
        <!--
          A combobox, not a select: a dimension the repository has no name for
          yet has to be namable here. "Any dimension" is the promise this panel
          makes, and a fixed list of the four that happen to exist is not any.
          Typing one that does not match offers it as one to create.
        -->
        <!--
          `ignore-filter`, and the matching is done here. Reka registers an
          item's text when it mounts, so a row that appears *because* of the
          query — the "New dimension" one — is not in the registry the filter
          reads, and gets dropped on the keystroke that should have created it.
          Typing then emptied the whole list. Same collision as the pinned
          suggestion in DSSuggestionCombobox, same answer.
        -->
        <DSCombobox
          v-model="dimension"
          ignore-filter
          open-on-click
          open-on-focus
          @click.stop
        >
          <!--
            The listener is on the anchor, not on the input. Reka's ComboboxInput
            passes its own `onInput` to the component it renders, so a listener
            bound to the input has to survive attr-merging through two component
            layers to reach the DOM — and when it does not, the typed dimension
            is never read and the "New dimension" row can never appear. The
            anchor is a real element, and input events bubble to it.
          -->
          <DSComboboxAnchor
            class="w-auto h-8 gap-1 px-2 border-transparent bg-transparent"
            @input="handleDimensionSearch"
          >
            <span class="shrink-0 text-xs font-medium text-muted-foreground">in</span>
            <DSComboboxInput
              class="w-24 text-xs font-medium text-muted-foreground"
              placeholder="a dimension"
              aria-label="Dimension for the new tag"
              :display-value="displayDimension"
            />
            <DSComboboxTrigger>
              <ChevronDown class="size-3.5" />
            </DSComboboxTrigger>
          </DSComboboxAnchor>
          <DSComboboxList class="min-w-48">
            <DSComboboxItem
              v-for="option in matchingDimensions"
              :key="option"
              :value="option"
            >
              {{ option }}
            </DSComboboxItem>
            <!--
              The typed dimension, when it is not one of the above. Offered as a
              row rather than accepted silently, so naming a new dimension is a
              decision the user makes and can see they made.
            -->
            <DSComboboxItem
              v-if="newDimension"
              :value="newDimension"
              class="text-accent-foreground"
            >
              <span class="flex items-center gap-2 min-w-0">
                <Plus class="size-3.5 shrink-0" />
                <span class="truncate">{{ newDimension }}</span>
              </span>
            </DSComboboxItem> 
          </DSComboboxList>
        </DSCombobox>
      </div>
    </div>

    <slot name="footer" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Check, ChevronDown, Plus, Search, Sparkles } from '@lucide/vue'

import { Tag, TagRef, TagSuggestion } from '../utils/types'

import { isSameTag, tagLabel } from '../utils/helpers'

import {
  DSBadge,
  DSCombobox,
  DSComboboxAnchor,
  DSComboboxInput,
  DSComboboxItem,
  DSComboboxList,
  DSComboboxTrigger,
  useDSFilter,
  DSInputGroup,
  DSInputGroupAddon,
  DSInputGroupInput,
  DSInputGroupText,
  DSThinking,
} from '@ds'

const {
  tags,
  selected = [],
  suggestions = [],
  dimensions,
  placeholder = 'Search tags',
} = defineProps<{
  /** Every tag in the repository. Filtering happens here, across dimensions. */
  tags: Tag[]
  /** Tags already applied, shown as selected rather than addable. */
  selected?: TagRef[]
  /**
   * Tags something proposed, each with its grounds. A suggestion carrying
   * `isNew` is drawn as a pair this repository does not have yet — which is
   * derived from the tags, never claimed by whoever suggested it.
   */
  suggestions?: (TagSuggestion & { isNew?: boolean })[]
  /** Suggestions have been asked for and have not arrived yet. */
  loading?: boolean
  /** Offers the "Ask AI" row — hidden once the assistant has been asked. */
  askable?: boolean
  /**
   * Dimensions offered when creating. Defaults to the distinct dimensions of
   * `tags`, which is right whenever the repository's own vocabulary is the
   * vocabulary — pass a list to narrow what is *offered*.
   *
   * It does not constrain what can be created: a dimension typed here and not
   * found is offered as a new one, because "any dimension" is the promise this
   * panel makes.
   */
  dimensions?: string[]
  placeholder?: string
}>()

const emit = defineEmits<{
  /** A tag was tapped: add it if unselected, remove it if selected. */
  (e: 'toggle', tag: TagRef): void
  (e: 'create', tag: TagRef): void
  /** The "Ask AI" row was tapped. */
  (e: 'ask'): void
}>()

const query = ref('')

/*
 * Matching spans dimension AND value, which is what "all dimensions" promises:
 * typing "priv" finds Strategy · Private credit and Access · Privilege alike.
 */
/*
 * Applied pairs the repository does not have: created in this panel and not
 * saved yet — nothing here persists until the screen that owns the form does.
 *
 * Derived rather than remembered. The caller already holds what is applied, so
 * closing and reopening the panel cannot lose them, and a tag that arrives from
 * the store on the next fetch simply stops being pending.
 */
const pendingTags = computed<Tag[]>(() =>
  selected
    .filter(applied => !tags.some(tag => isSameTag(tag, applied)))
    .map(applied => ({
      id: '',
      tag_name: applied.tag_name,
      tag_value: applied.tag_value,
    }) as Tag),
)

/** A pending tag has no id: nothing has given it one yet. */
const isPending = (tag: Tag) => !tag.id

/** Pending first — they are what the user just did, and they are unsaved. */
const catalogue = computed(() => [...pendingTags.value, ...tags])

const matches = computed(() => {
  const term = query.value.trim().toLowerCase()
  if (!term) return catalogue.value

  return catalogue.value.filter(
    tag =>
      tag.tag_value?.toLowerCase().includes(term)
      || tag.tag_name?.toLowerCase().includes(term),
  )
})

/** The exact pair the create row would make, when the list already has it. */
const alreadyListed = computed(() => {
  const tagValue = query.value.trim()
  const tagName = effectiveDimension.value
  if (!tagValue || !tagName) return false

  return catalogue.value.some(tag => isSameTag(tag, { tag_name: tagName, tag_value: tagValue }))
})

// A suggestion already in the list below would read as a duplicate.
const visibleSuggestions = computed(() => {
  const term = query.value.trim().toLowerCase()
  if (!term) return suggestions

  return suggestions.filter(
    suggestion =>
      suggestion.tag_value?.toLowerCase().includes(term)
      || suggestion.tag_name?.toLowerCase().includes(term),
  )
})

const dimensionOptions = computed(
  () => dimensions ?? [...new Set(tags.map(tag => tag.tag_name))].filter(Boolean),
)

/*
 * Empty until the user picks one. It used to default to the first dimension in
 * the list, which with the repository's real vocabulary means "whichever sorts
 * first" — so the panel offered to file a new tag under a dimension nobody had
 * chosen, and Create was enabled to do it. The comment above this block says
 * the dimension is chosen rather than assumed; this is what makes that true.
 */
const dimension = ref('')

/*
 * What the user has typed into the dimension field, which is not the same thing
 * as the dimension they have chosen: it is only a candidate until they pick it,
 * either from the list or as a new one.
 */
const dimensionQuery = ref('')

/** Reka's own matcher — accent-insensitive, so "Reserve" finds "Réserve". */
const { contains } = useDSFilter({ sensitivity: 'base' })

/** The dimensions the typed text matches. Ours to compute, since we ignore Reka's. */
const matchingDimensions = computed(() => {
  const typed = dimensionQuery.value.trim()
  if (!typed) return dimensionOptions.value

  return dimensionOptions.value.filter(option => contains(option, typed))
})

/** The dimension the typed text names, when it names one that exists. */
const matchedDimension = computed(() => {
  const typed = dimensionQuery.value.trim()
  if (!typed) return ''

  return (
    dimensionOptions.value.find(
      option => option.trim().toLowerCase() === typed.toLowerCase(),
    ) ?? ''
  )
})

/**
 * The typed dimension, when it names one the repository does not have.
 *
 * Only when the text matches *nothing*. While it still narrows to existing
 * dimensions the user is searching, not naming — typing "cat" with "Category"
 * on screen must not offer to create a dimension called "cat", and must not let
 * Create make one.
 */
const newDimension = computed(() => {
  const typed = dimensionQuery.value.trim()
  if (!typed) return ''
  if (matchedDimension.value || matchingDimensions.value.length) return ''

  return typed
})

/*
 * What Create will use, in order of how explicit the user was:
 *
 *   1. one they picked from the list;
 *   2. one they typed the full name of — spelled as the repository spells it,
 *      so "asset class" does not fork the vocabulary;
 *   3. one they typed that does not exist yet, which creating will add.
 *
 * Typing is enough on purpose. Requiring the dropdown row to be found and
 * clicked made naming a new dimension depend on a panel that can close under
 * the user, and the row is a shortcut, not the mechanism. What will happen is
 * legible either way: the field holds the dimension the button names.
 */
const effectiveDimension = computed(
  () => dimension.value.trim() || matchedDimension.value || newDimension.value,
)

/** The field shows the chosen dimension; the list is what it is chosen from. */
const displayDimension = (value: unknown) => String(value ?? '')

const handleDimensionSearch = (event: Event) => {
  dimensionQuery.value = (event.target as HTMLInputElement).value
}

const isSelected = (tag: TagRef) =>
  selected.some(item => isSameTag(item, tag))

const handleCreate = () => {
  const tagValue = query.value.trim()
  const tagName = effectiveDimension.value
  if (!tagValue || !tagName) return

  emit('create', { tag_name: tagName, tag_value: tagValue })
  dimensionQuery.value = ''
  query.value = ''
}
</script>
